import { useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash';

/**
 * Creates a debounced version of a callback function that automatically cleans up on unmount.
 * The debounced function is recreated when delay or options change.
 *
 * @template T - The type of the callback function
 * @param callback - The callback function to debounce
 * @param delay - The delay in milliseconds for the debounce
 * @param options - Optional lodash debounce options (leading, trailing, maxWait)
 * @returns A debounced version of the callback that cancels on unmount
 *
 * @example
 * ```tsx
 * const handleSearch = useDebouncedCallback((value: string) => {
 *     onSearchChange(value);
 * }, 250);
 *
 * // With options
 * const handleClick = useDebouncedCallback(() => {
 *     onClick();
 * }, 1000, { leading: true, trailing: false });
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useDebouncedCallback = <T extends (...args: any[]) => any>(
    callback: T,
    delay: number,
    options?: Parameters<typeof debounce>[2]
) => {
    // Always point to the latest callback (avoids stale state/props)
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    // Create the debounced function once per delay/options
    const debounced = useMemo(
        () =>
            debounce(
                (...args: Parameters<T>) => callbackRef.current(...args),
                delay,
                options
            ),
        [delay, options]
    );

    // Cancel on unmount (and when the debounced fn instance changes)
    useEffect(() => {
        return () => {
            debounced.cancel();
        };
    }, [debounced]);

    return debounced;
};
